import React, { useMemo } from 'react'
import { ContainerProps, Skeleton, ProgressiveImage, Container, Link } from '@thirstycamel/ui'
import useMedia from 'use-media'
import { useQuery } from 'react-query'
import dayjs from 'dayjs'

import { useStore } from '../../store/hooks'
import imageProxy from '../../utils/imageProxy'
import { backend } from '../../utils/backend'
import { cmsUseNew } from '../../utils/cms'
import { useAdvertsQuery, Advert as WPAdvert } from '../../../generated'

const ADVERTS_FETCH_LIMIT = 15
const ADVERTS_SHOWING_LIMIT = 10

/* The banner shape returned by the core `/banners/public` endpoint. */
interface PublicBanner {
  id: string
  title: string
  link?: string
  isLinkExternal?: boolean
  imageDesktop?: { filename: string }
  imageMobile?: { filename: string }
}

export interface AdvertProps extends Omit<ContainerProps, 'position'> {
  position: 'HOME_TOP' | 'HOME_MIDDLE' | 'HOME_BOTTOM' | 'PRODUCT'
}

const handleFilterWordPress = (advert: { node: WPAdvert }) => {
  return !advert?.node?.acf?.endsAt || dayjs(advert.node.acf.endsAt).isAfter(Date.now())
}

const AdvertCore = ({ position, ...restProps }: AdvertProps) => {
  const isMobile = useMedia({ maxWidth: '30em' })
  const location = useStore(s => s.location)
  const selectedStore = useStore(s => s.store.selectedStore)

  const region = selectedStore?.region || location.region || 'VIC'

  /* Active window and region filtering are applied server-side by the endpoint. */
  const query = useQuery<PublicBanner[]>(
    ['banners/public', position, region],
    () =>
      backend<PublicBanner[]>('banners/public', {
        params: { placement: position, region },
      }),
    {
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      staleTime: 30000,
    },
  )

  const adverts = useMemo(() => query.data?.slice(0, ADVERTS_SHOWING_LIMIT), [query.data])

  if (!adverts?.length) {
    if (query.isFetching) {
      return (
        <Container {...restProps}>
          <Skeleton paddingBottom={['calc(1 / 5 * 100%)', , 'calc(1 / 8 * 100%)']} />
        </Container>
      )
    }

    return null
  }

  const randomIndex = Math.floor(Math.random() * adverts.length)
  const advert = adverts[randomIndex]

  /* Determine whether to use the desktop or mobile image for the banners. */
  const imageKey = isMobile ? 'imageMobile' : 'imageDesktop'

  const image = (
    <ProgressiveImage
      display="block"
      src={imageProxy(advert?.[imageKey]?.filename)}
      size="cover"
      backgroundPosition="center"
      w="100%"
      h="100%"
      paddingBottom={['calc(1 / 5 * 100%)', , 'calc(1 / 8 * 100%)']}
      border="1px"
      borderColor="gray.200"
    />
  )

  return (
    <Container px={[4, 5, 6, 8]} {...restProps}>
      {advert?.link ? <Link href={advert?.link}>{image}</Link> : image}
    </Container>
  )
}

const AdvertWordPress = ({ position, ...restProps }: AdvertProps) => {
  const isMobile = useMedia({ maxWidth: '30em' })
  const location = useStore(s => s.location)
  const selectedStore = useStore(s => s.store.selectedStore)

  const region = selectedStore?.region || location.region || 'VIC'

  const [query] = useAdvertsQuery({
    variables: { first: ADVERTS_FETCH_LIMIT, region, position },
  })

  const adverts = useMemo(
    () => query.data?.adverts?.edges?.filter(handleFilterWordPress).slice(0, ADVERTS_SHOWING_LIMIT),
    [query.data],
  )

  if (!adverts?.length) {
    if (query.fetching) {
      return (
        <Container {...restProps}>
          <Skeleton paddingBottom={['calc(1 / 5 * 100%)', , 'calc(1 / 8 * 100%)']} />
        </Container>
      )
    }

    return null
  }

  const randomIndex = Math.floor(Math.random() * adverts.length)
  const advert = adverts[randomIndex]?.node

  /* Determine whether to use the desktop or mobile image for the banners. */
  const imageKey = isMobile ? 'imageMobile' : 'imageDesktop'

  const image = (
    <ProgressiveImage
      display="block"
      src={imageProxy(advert?.acf[imageKey]?.sourceUrl, { wp: true })}
      size="cover"
      backgroundPosition="center"
      w="100%"
      h="100%"
      paddingBottom={['calc(1 / 5 * 100%)', , 'calc(1 / 8 * 100%)']}
      border="1px"
      borderColor="gray.200"
    />
  )

  return (
    <Container px={[4, 5, 6, 8]} {...restProps}>
      {advert?.acf.link ? <Link href={advert?.acf.link}>{image}</Link> : image}
    </Container>
  )
}

export const Advert = (props: AdvertProps) =>
  cmsUseNew() ? <AdvertCore {...props} /> : <AdvertWordPress {...props} />

export default Advert
